import { formatStorePrice } from '../utils/storePrice.js'
/**
 * Apple In-App Purchases service
 * Uses cordova-plugin-purchase for StoreKit integration
 */
import { Capacitor } from '@capacitor/core'
import { ref, computed } from 'vue'
import { iosSalesDisabled } from './sales.js'
import { reportClientEvent } from '../errorTracking.js'

// IAP product IDs (must match App Store Connect)
const PRODUCT_IDS = {
  '1_month': 'ru.zozhnik.zoya.1month',
  '12_months': 'ru.zozhnik.zoya.12months',
}

// Plan ID mapping (reverse)
const PLAN_MAP = { monthly: '1_month', annual: '12_months' }

const products = ref({})
const iapReady = ref(false)
const iapError = ref(null)
const purchaseInProgress = ref(false)
const restoreInProgress = ref(false)

let store = null
let _resolveVerified = null
let _rejectVerified = null

const isIOS = Capacitor.getPlatform() === 'ios'

let initPromise = null
let priceRefreshPromise = null
let iapUserId = null

// appAccountToken для StoreKit: UUID с номером пользователя в последних 12
// hex. Apple возвращает его в подписанной транзакции, и вебхук сервера
// (api/payment.js) по нему включает Премиум, даже если проверка чека с
// телефона не состоялась. Формат — как appAccountTokenFor в
// lib/appleNotifications.js, менять вместе.
function appAccountTokenFor(userId) {
  const n = Number(userId)
  if (!Number.isSafeInteger(n) || n <= 0) return undefined
  return '00000000-0000-4000-8000-' + n.toString(16).padStart(12, '0')
}

function report(message, meta) {
  try { reportClientEvent(`[IAP] ${message}`, meta) } catch { /* отчет не должен ломать покупку */ }
}

async function initIAP(userId) {
  // Аварийный выключатель продаж (services/sales.js): StoreKit не трогаем
  if (!isIOS || iosSalesDisabled) return
  // Кто покупает — для appAccountToken; экраны передают при каждом открытии
  if (userId) iapUserId = userId
  // Экраны подписки и управления зовут init каждый по себе — StoreKit
  // инициализируется один раз, повторный вызов ждёт первый
  if (initPromise) {
    await initPromise
    return refreshIAPPrices()
  }
  initPromise = initIAPOnce()
  return initPromise
}

// StoreKit remains the price authority, including after an App Store account change.
// Concurrent paywalls share one refresh; the plugin throttles repeated requests.
async function refreshIAPPrices() {
  if (!isIOS || iosSalesDisabled || !iapReady.value || !store) return
  if (priceRefreshPromise) return priceRefreshPromise
  priceRefreshPromise = Promise.resolve().then(() => store.update())
    .catch(err => report('не удалось обновить цены App Store', { message: err?.message }))
    .finally(() => { priceRefreshPromise = null })
  return priceRefreshPromise
}

// Скрипт cordova-plugin-purchase ничего не экспортирует: он кладёт
// CdvPurchase в window (в Capacitor его подключает и нативный
// cordova_plugins.js), причём через setTimeout(0). До 23.10.2026 здесь стояло
// `const { CdvPurchase } = await import(...)` — undefined, и инициализация
// StoreKit падала на первой строке: в iOS продажи были выключены, на
// телефоне этот код до того ни разу не выполнялся.
function deviceReady(timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (!window.cordova) { resolve(); return }
    const t = setTimeout(resolve, timeoutMs)
    // deviceready у Cordova «липкий»: подписка после события срабатывает сразу
    document.addEventListener('deviceready', () => { clearTimeout(t); resolve() }, { once: true })
  })
}

async function loadCdvPurchase() {
  await deviceReady()
  if (!window.CdvPurchase?.store) await import('cordova-plugin-purchase')
  for (let i = 0; i < 40 && !window.CdvPurchase?.store; i++) {
    await new Promise((r) => setTimeout(r, 50))
  }
  if (!window.CdvPurchase?.store) throw new Error('Плагин покупок не загрузился')
  return window.CdvPurchase
}

async function initIAPOnce() {
  try {
    const CdvPurchase = await loadCdvPurchase()
    store = CdvPurchase.store
    store.minTimeBetweenUpdates = 30000
    // Покупка помечается аккаунтом: сырой UUID, без «обфускации» плагина
    store.obfuscator = 'disabled'
    store.applicationUsername = () => appAccountTokenFor(iapUserId)
    store.error((err) => {
      // Отмена покупки человеком — не сбой
      if (err?.code === CdvPurchase.ErrorCode.PAYMENT_CANCELLED) return
      report('ошибка StoreKit', { code: err?.code, message: err?.message, productId: err?.productId })
    })

    // Register products
    store.register([
      {
        id: PRODUCT_IDS['1_month'],
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
      },
      {
        id: PRODUCT_IDS['12_months'],
        type: CdvPurchase.ProductType.PAID_SUBSCRIPTION,
        platform: CdvPurchase.Platform.APPLE_APPSTORE,
      },
    ])

    // Server-side receipt verification
    // body — Validator.Request.Body плагина: чек App Store лежит в
    // body.transaction.appStoreReceipt (StoreKit 1, base64), продукт —
    // в body.products. Колбэк ждёт SuccessPayload с id и transaction.
    store.validator = async (body, callback) => {
      try {
        const token = localStorage.getItem('zoya-token')
        const user = JSON.parse(localStorage.getItem('zoya-user') || 'null')
        // StoreKit присылает старые покупки уже при открытии пейвола.
        // Нельзя привязывать их к гостю или к сменившемуся аккаунту.
        if (!token || !user?.id || user.is_guest || user.id !== iapUserId) {
          callback({ ok: false, code: 6778001, message: 'Для восстановления подписки войдите в свой аккаунт' })
          return
        }
        const tx = body?.transaction || {}
        const appStoreReceipt = tx.appStoreReceipt || tx.transactionReceipt
        if (!appStoreReceipt) {
          report('нет чека App Store для проверки', { type: tx.type, user: iapUserId })
          callback({ ok: false, code: 6778003, message: 'Нет чека App Store' })
          return
        }
        const response = await fetch('https://zoya.zozhnik.ru/api/payment/apple-verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({
            receipt: appStoreReceipt,
            productId: body?.products?.[0]?.id,
          }),
        })
        const data = await response.json()
        if (data.ok) {
          callback({ ok: true, data: { id: body?.id || 'zoya', latest_receipt: true, transaction: tx } })
        } else {
          report('сервер не принял чек', { status: response.status, error: data.error })
          callback({ ok: false, code: 6778001, message: data.error || 'Verification failed' })
        }
      } catch (err) {
        report('проверка чека не дошла до сервера', { message: err.message })
        callback({ ok: false, message: err.message })
      }
    }

    // Lifecycle handlers
    store.when()
      .productUpdated((product) => {
        products.value = { ...products.value, [product.id]: product }
        if (product.pricing?.price) iapError.value = null
      })
      .approved((transaction) => {
        transaction.verify()
      })
      .verified((receipt) => {
        receipt.finish()
        if (_resolveVerified) {
          _resolveVerified()
          _resolveVerified = null
          _rejectVerified = null
        }
      })
      .unverified((receipt) => {
        if (_rejectVerified) {
          _rejectVerified(new Error(receipt.payload?.message || 'Verification failed'))
          _resolveVerified = null
          _rejectVerified = null
        }
      })

    await store.initialize([CdvPurchase.Platform.APPLE_APPSTORE])
    iapReady.value = true
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void refreshIAPPrices()
    })
    // Продукты приходят вслед за инициализацией. Не пришли за 8 секунд —
    // App Store их не отдал (не заведены, не одобрены для песочницы, нет
    // соглашения): говорим об этом экранам, а не показываем вечное «…»
    setTimeout(() => {
      const loaded = Object.values(products.value).some((p) => p?.pricing?.priceMicros)
      if (!loaded && !iapError.value) iapError.value = 'App Store не вернул подписки'
    }, 8000)
  } catch (err) {
    console.error('IAP init error:', err)
    iapError.value = err.message
  }
}

/**
 * Start a purchase flow
 * @param {string} planId - '1_month' or '12_months' (or 'monthly'/'annual')
 * @returns {Promise<void>} resolves when purchase is verified, rejects on error/cancel
 */
async function purchaseProduct(planId, { isActive, timeoutMs = 90000 } = {}) {
  if (!store) throw new Error('IAP not initialized')

  // Normalize plan IDs
  const normalizedPlan = PLAN_MAP[planId] || planId
  const productId = PRODUCT_IDS[normalizedPlan]
  if (!productId) throw new Error('Unknown plan: ' + planId)

  const product = store.get(productId)
  if (!product) throw new Error('Product not found: ' + productId)

  const offer = product.getOffer()
  if (!offer) throw new Error('No offer available for: ' + productId)

  purchaseInProgress.value = true

  try {
    // Create a promise that resolves when the purchase is verified
    const verifiedPromise = new Promise((resolve, reject) => {
      _resolveVerified = resolve
      _rejectVerified = reject
    })

    const orderResult = await store.order(offer)
    if (orderResult?.isError) {
      throw orderResult
    }

    // Подтверждение — первым из двух путей: чек с телефона (verified) или
    // уведомление Apple на сервер по appAccountToken, которое видно по
    // аккаунту (isActive). Если чека на телефоне нет, плагин молча бросает
    // проверку, и ждать одного verified значило бы ждать вечно (TestFlight,
    // 23.10.2026). Через 90 секунд — честное сообщение, а не тишина.
    let stop = false
    const viaServer = (async () => {
      if (!isActive) return new Promise(() => {})
      for (let i = 0; i < 30 && !stop; i++) {
        await new Promise((r) => setTimeout(r, 3000))
        if (!stop && await isActive().catch(() => false)) return 'server'
      }
      return new Promise(() => {})
    })()
    const timeout = new Promise((resolve) => setTimeout(() => resolve('timeout'), timeoutMs))
    const how = await Promise.race([verifiedPromise.then(() => 'receipt'), viaServer, timeout])
    stop = true
    _resolveVerified = null
    _rejectVerified = null
    if (how === 'timeout') report('покупка без подтверждения', { productId, user: iapUserId, timeoutMs })
    return how
  } finally {
    purchaseInProgress.value = false
  }
}

// Покупка тарифа целиком — с ответом для экрана:
//   activated — подписка с этим тарифом уже на аккаунте;
//   scheduled — у человека уже была подписка App Store, и Apple перенёс смену
//     тарифа на конец периода (понижение или кросс-грейд в группе подписок —
//     23.10.2026 месяц → год так и ушёл «DOWNGRADE»: годовая стояла в группе
//     ниже месячной); startsAt — когда начнётся новый тариф;
//   pending — оплата прошла, подтверждения пока нет, придёт уведомлением.
// refreshUser/getUser — от экрана (pinia), чтобы не тянуть стор сюда.
async function buyApplePlan(planId, { refreshUser, getUser }) {
  const plan = PLAN_MAP[planId] || planId
  const activeApple = (u) => !!(u?.apple_managed && u?.subscription_end && new Date(u.subscription_end) > new Date())
  const before = getUser()
  const hadApple = activeApple(before)
  const isActive = async () => {
    await refreshUser()
    const u = getUser()
    return activeApple(u) && u.subscription_plan === plan
  }
  // Смена тарифа при действующей подписке: повышение Apple включает сразу,
  // понижение — в конце периода; полторы минуты ждать незачем
  await purchaseProduct(plan, { isActive, timeoutMs: hadApple ? 30000 : 90000 })
  await refreshUser().catch(() => {})
  const u = getUser()
  if (activeApple(u) && u.subscription_plan === plan) return { status: 'activated' }
  if (hadApple) return { status: 'scheduled', startsAt: u?.subscription_end || before?.subscription_end || null }
  return { status: 'pending' }
}

// Управление подписками App Store. Встроенный браузер (Browser.open) адрес
// apps.apple.com/account/subscriptions не открывает — его понимает только
// приложение App Store, куда его и отправляет нативная часть плагина
// (openURL). В TestFlight там видны только настоящие подписки, подписки
// песочницы — в Настройки → App Store → Sandbox Account.
async function manageSubscriptions() {
  try {
    if (store?.manageSubscriptions) {
      const err = await store.manageSubscriptions(window.CdvPurchase?.Platform?.APPLE_APPSTORE)
      if (!err) return
    }
  } catch { /* ниже — запасной путь */ }
  window.location.href = 'itms-apps://apps.apple.com/account/subscriptions'
}

/**
 * Restore previously purchased subscriptions.
 * Обязателен по гайдлайну App Store 3.1.1: пользователь, переустановивший
 * приложение или сменивший устройство, должен уметь вернуть доступ без повторной оплаты.
 *
 * Чеки, которые вернёт StoreKit, уходят в тот же store.validator, что и при покупке,
 * — то есть подписка восстанавливается на сервере. Обновить состояние
 * пользователя после вызова — задача вызывающего кода.
 *
 * @returns {Promise<void>} резолвится, когда StoreKit закончил обход покупок
 */
async function restorePurchases() {
  if (!store) throw new Error('IAP not initialized')

  restoreInProgress.value = true
  try {
    // CdvPurchase возвращает IError вместо throw, если восстановление не удалось
    const err = await store.restorePurchases()
    if (err) throw new Error(err.message || 'Не удалось восстановить покупки')
  } finally {
    restoreInProgress.value = false
  }
}

// Цены из App Store для экранов: Apple сверяет цифры на экране с ценой
// подписки, поэтому в iOS рублёвые константы не показываем, только то, что
// вернул StoreKit. null — продукты ещё не загрузились.
function fmtMicros(micros, currency) {
  try {
    return formatStorePrice(micros, currency)
  } catch {
    return `${Math.round(micros / 1_000_000)} ${currency}`
  }
}

const applePrices = computed(() => {
  const m = products.value[PRODUCT_IDS['1_month']]?.pricing
  const y = products.value[PRODUCT_IDS['12_months']]?.pricing
  if (!m?.priceMicros && !y?.priceMicros) return null
  const out = { monthly: null, annualTotal: null, annualPerMonth: null, savingPerMonth: null, badgePercent: null }
  if (m?.priceMicros) out.monthly = m.price || fmtMicros(m.priceMicros, m.currency)
  if (y?.priceMicros) {
    out.annualTotal = y.price || fmtMicros(y.priceMicros, y.currency)
    out.annualPerMonth = fmtMicros(Math.round(y.priceMicros / 12), y.currency)
  }
  // Обе подписки в App Store продлеваются сами — у годовой нет «разового платежа»
  out.annualAutoRenew = true
  if (m?.priceMicros && y?.priceMicros && m.currency === y.currency) {
    const perMonth = y.priceMicros / 12
    if (perMonth < m.priceMicros) {
      out.savingPerMonth = fmtMicros(Math.round(m.priceMicros - perMonth), y.currency)
      out.badgePercent = Math.round(100 * (1 - perMonth / m.priceMicros))
    }
  }
  return out
})

/**
 * Get the localized price string for a plan
 * @param {string} planId - '1_month' or '12_months' (or 'monthly'/'annual')
 */
function getProductPrice(planId) {
  const normalizedPlan = PLAN_MAP[planId] || planId
  const productId = PRODUCT_IDS[normalizedPlan]
  const product = products.value[productId]
  if (!product?.pricing) return null
  return product.pricing.price || (product.pricing.priceMicros != null
    ? fmtMicros(product.pricing.priceMicros, product.pricing.currency) : null)
}

/**
 * Get the pricing object for a plan
 */
function getProductPricing(planId) {
  const normalizedPlan = PLAN_MAP[planId] || planId
  const productId = PRODUCT_IDS[normalizedPlan]
  const product = products.value[productId]
  return product?.pricing || null
}

export {
  initIAP,
  applePrices,
  appAccountTokenFor,
  buyApplePlan,
  manageSubscriptions,
  purchaseProduct,
  restorePurchases,
  getProductPrice,
  getProductPricing,
  products,
  iapReady,
  iapError,
  purchaseInProgress,
  restoreInProgress,
  isIOS,
  PRODUCT_IDS,
}
